import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { defaultCustomRange, isCustomRangeValid, todayInputValue } from '../../utils/dateInput';
import StatCard from '../common/StatCard';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import VenueTrendChart from './VenueTrendChart';
import '../../assets/css/business-overview.css';

/**
 * Venue Analytics & Venue Performance (PDF Pages 8 & 9)
 * Purely venue-centric metrics filtered by selected Venue and Period.
 */
const VendorAnalyticsTable = () => {
    const { auth } = useAuth();
    const [selectedVenue, setSelectedVenue] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState('30d');
    const [customRange, setCustomRange] = useState(defaultCustomRange);
    const [venueList, setVenueList] = useState([]);
    
    const [data, setData] = useState({
        kpis: {
            reservations: 0,
            venue_saves: 0,
            content_saves: 0,
            unique_savers: 0,
            total_reviews: 0,
            avg_rating: 0
        },
        performance: [],
        total: 0
    });

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const requestRef = useRef(null);

    // Search debounce
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(handler);
    }, [search]);

    // Cancel any in-flight request on unmount
    useEffect(() => () => requestRef.current?.abort(), []);

    const fetchVenuePerformance = useCallback(async () => {
        if (selectedPeriod === 'custom' && !isCustomRangeValid(customRange)) return;

        // Cancel the previous request so a slow response can't overwrite newer data
        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;

        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams({
                venue_id: selectedVenue,
                period: selectedPeriod,
                search: debouncedSearch,
                limit: pageSize,
                offset: (page - 1) * pageSize
            });
            if (selectedPeriod === 'custom') {
                params.set('from', customRange.from);
                params.set('to', customRange.to);
            }

            const res = await auth.fetch(`${config.API_BASE_URL}/api/stats/venue-performance?${params.toString()}`, {
                signal: controller.signal
            });

            if (!res.ok) {
                let message = 'Failed to load venue performance';
                try {
                    const body = await res.json();
                    if (body?.error) message = body.error;
                } catch {
                    // Response had no JSON body; keep the generic message
                }
                throw new Error(res.status === 403 ? 'You do not have permission to view venue analytics' : message);
            }

            const json = await res.json();

            if (json.venues) {
                setVenueList(json.venues);
            }
            setData(json);
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('Error fetching venue analytics:', err);
            setError(err.message || 'Unable to load venue analytics');
        } finally {
            if (requestRef.current === controller) setLoading(false);
        }
    }, [auth, selectedVenue, selectedPeriod, customRange, debouncedSearch, page]);

    useEffect(() => {
        fetchVenuePerformance();
    }, [fetchVenuePerformance]);

    const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

    return (
        <div className="d-flex flex-column gap-4">
            {/* 1. Header with Global Venue & Period Filters */}
            <div className="hs-modern-card p-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div>
                        <p className="text-secondary-light text-sm mb-0">
                            Comprehensive platform oversight of venue performance, reservations, customer saves, and reviews.
                        </p>
                    </div>

                    {/* Top Filters: Venue & Period */}
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-xs fw-semibold text-secondary-light text-nowrap">Venue:</span>
                            <select
                                className="form-select form-select-sm radius-8"
                                style={{ minWidth: '180px' }}
                                value={selectedVenue}
                                onChange={(e) => {
                                    setSelectedVenue(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">All Venues</option>
                                {venueList.map(v => (
                                    <option key={v.id} value={v.id}>{v.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <span className="text-xs fw-semibold text-secondary-light text-nowrap">Period:</span>
                            <select
                                className="form-select form-select-sm radius-8"
                                style={{ minWidth: '140px' }}
                                value={selectedPeriod}
                                onChange={(e) => {
                                    setSelectedPeriod(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="7d">7 Days</option>
                                <option value="30d">30 Days</option>
                                <option value="3m">3 Months</option>
                                <option value="6m">6 Months</option>
                                <option value="12m">12 Months</option>
                                <option value="custom">Custom</option>
                            </select>
                        </div>

                        {selectedPeriod === 'custom' && (
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    type="date"
                                    aria-label="From date"
                                    className="form-control form-control-sm radius-8"
                                    value={customRange.from}
                                    max={customRange.to || todayInputValue()}
                                    onChange={(e) => {
                                        setCustomRange((range) => ({ ...range, from: e.target.value }));
                                        setPage(1);
                                    }}
                                />
                                <span className="text-xs fw-semibold text-secondary-light">to</span>
                                <input
                                    type="date"
                                    aria-label="To date"
                                    className="form-control form-control-sm radius-8"
                                    value={customRange.to}
                                    min={customRange.from}
                                    max={todayInputValue()}
                                    onChange={(e) => {
                                        setCustomRange((range) => ({ ...range, to: e.target.value }));
                                        setPage(1);
                                    }}
                                />
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={fetchVenuePerformance}
                            disabled={loading}
                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 radius-8 px-3"
                        >
                            <Icon icon="solar:restart-bold" className={loading ? 'spin' : ''} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* 2. 6 Static KPI Cards (PDF Page 8) */}
            <div className="row row-cols-xxl-6 row-cols-lg-3 row-cols-md-2 row-cols-1 g-3">
                {/* Reservations */}
                <div className="col">
                    <StatCard 
                        title="Reservations"
                        value={loading ? '...' : (data.kpis?.reservations || 0).toLocaleString()}
                        icon="solar:calendar-date-bold"
                        iconBg="var(--hs-info-bg)"
                        iconColor="var(--hs-info)"
                        helperText="Bookings received"
                    />
                </div>

                {/* Venue Saves */}
                <div className="col">
                    <StatCard 
                        title="Venue Saves"
                        value={loading ? '...' : (data.kpis?.venue_saves || 0).toLocaleString()}
                        icon="solar:heart-bold"
                        iconBg="rgba(239, 68, 68, 0.1)"
                        iconColor="#ef4444"
                        helperText="Venue bookmarks"
                    />
                </div>

                {/* Content Saves */}
                <div className="col">
                    <StatCard 
                        title="Content Saves"
                        value={loading ? '...' : (data.kpis?.content_saves || 0).toLocaleString()}
                        icon="solar:document-text-bold"
                        iconBg="var(--hs-warning-bg)"
                        iconColor="var(--hs-warning)"
                        helperText="Articles saved"
                    />
                </div>

                {/* Unique Savers */}
                <div className="col">
                    <StatCard 
                        title="Unique Savers"
                        value={loading ? '...' : (data.kpis?.unique_savers || 0).toLocaleString()}
                        icon="solar:users-group-rounded-bold"
                        iconBg="var(--hs-primary-50)"
                        iconColor="var(--hs-primary-600)"
                        helperText="Distinct customers"
                    />
                </div>

                {/* Total Reviews */}
                <div className="col">
                    <StatCard 
                        title="Total Reviews"
                        value={loading ? '...' : (data.kpis?.total_reviews || 0).toLocaleString()}
                        icon="solar:chat-round-line-bold"
                        iconBg="var(--hs-warning-bg)"
                        iconColor="var(--hs-warning)"
                        helperText="Customer feedback"
                    />
                </div>

                {/* Average Rating */}
                <div className="col">
                    <StatCard 
                        title="Average Rating"
                        value={loading ? '...' : (data.kpis?.avg_rating ? `${data.kpis.avg_rating} / 5.0 ★` : 'No ratings')}
                        icon="solar:star-bold"
                        iconBg="var(--hs-success-bg)"
                        iconColor="var(--hs-success)"
                        helperText="Weighted score"
                    />
                </div>
            </div>

            {/* Trend charts follow the Venue and Period filters */}
            <div className="row g-3 mb-24">
                <div className="col-xl-6 col-12">
                    <VenueTrendChart
                        title="Reservations Trend"
                        seriesName="Reservations"
                        unit="reservation"
                        data={data.trends?.reservations || []}
                        loading={loading}
                    />
                </div>
                <div className="col-xl-6 col-12">
                    <VenueTrendChart
                        title="Saves Trend"
                        seriesName="Saves"
                        unit="save"
                        color="#10B981"
                        data={data.trends?.saves || []}
                        loading={loading}
                    />
                </div>
            </div>

            {/* 3. Venue Performance Table (formerly Vendor Engagement Ledger) */}
            <div className="hs-modern-card">
                <div className="hs-modern-card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div>
                        <h5 className="hs-modern-card-title mb-1">Venue Performance</h5>
                        <span className="text-secondary-light text-xs">
                            Performance metrics and customer engagement broken down per venue.
                        </span>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <div className="position-relative" style={{ width: '240px' }}>
                            <input
                                type="text"
                                className="form-control form-control-sm ps-5 radius-8"
                                placeholder="Search venue..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <Icon 
                                icon="solar:magnifer-linear" 
                                className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary-light" 
                            />
                        </div>
                    </div>
                </div>

                <div className="p-0">
                    {loading ? (
                        <LoadingState message="Loading venue performance data..." />
                    ) : error ? (
                        <div className="p-4 text-center text-danger text-sm">{error}</div>
                    ) : !data.performance?.length ? (
                        <EmptyState 
                            icon="solar:buildings-3-bold"
                            title="No venue activity found"
                            description="No performance records found for the selected venue and time period."
                        />
                    ) : (
                        <div className="table-responsive">
                            <table className="hs-table-clean">
                                <thead>
                                    <tr>
                                        <th scope="col" style={{ width: '30%' }}>Venue</th>
                                        <th scope="col" style={{ width: '14%' }}>Reservations</th>
                                        <th scope="col" style={{ width: '14%' }}>Total Saves</th>
                                        <th scope="col" style={{ width: '14%' }}>Unique Savers</th>
                                        <th scope="col" style={{ width: '14%' }}>Reviews</th>
                                        <th scope="col" style={{ width: '14%' }}>Average Rating</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.performance.map(row => (
                                        <tr key={row.venue_id}>
                                            <td>
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="w-40-px h-40-px rounded-8 bg-neutral-100 text-primary-600 d-flex align-items-center justify-content-center flex-shrink-0">
                                                        <Icon icon="solar:shop-2-bold" className="text-lg" />
                                                    </div>
                                                    <div>
                                                        <span className="fw-semibold d-block text-primary-light">
                                                            {row.venue_name}
                                                        </span>
                                                        <span className="badge bg-primary-50 text-primary-600 border border-primary-100 px-2 py-0.5 radius-4 text-2xs fw-medium mt-1">
                                                            {row.category_name || 'General'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="fw-semibold text-primary-light">
                                                    {(row.reservations_count || 0).toLocaleString()}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-semibold text-danger d-inline-flex align-items-center gap-1">
                                                    <Icon icon="solar:heart-bold" className="text-xs" />
                                                    {(row.total_saves || 0).toLocaleString()}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="text-secondary-light">
                                                    {(row.unique_savers || 0).toLocaleString()}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="text-secondary-light">
                                                    {(row.reviews_count || 0).toLocaleString()}
                                                </span>
                                            </td>
                                            <td>
                                                {row.avg_rating > 0 ? (
                                                    <span className="badge bg-warning-50 text-warning-700 border border-warning-200 px-2 py-1 radius-4 text-xs fw-semibold">
                                                        ★ {row.avg_rating} / 5.0
                                                    </span>
                                                ) : (
                                                    <span className="text-secondary-light text-xs">No ratings</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="p-3 border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <span className="text-xs text-secondary-light">
                            Showing page {page} of {totalPages} ({data.total} venues)
                        </span>
                        <div className="d-flex align-items-center gap-1">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary radius-8 px-2 py-1 text-xs"
                                disabled={page <= 1}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            <span className="px-2 text-xs fw-semibold">{page} / {totalPages}</span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary radius-8 px-2 py-1 text-xs"
                                disabled={page >= totalPages}
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VendorAnalyticsTable;
